export const guides = [
  {
    id: 1,
    slug: "ziyarat-of-imam-hussain-karbala-guide",
    title: "A Complete Guide to Ziyarat of Imam Hussain (AS) in Karbala",
    excerpt:
      "Plan your ziyarat to Imam Hussain’s shrine in Karbala. History, shrine details, dua, etiquette, safety tips, and travel guide for pilgrims visiting Iraq.",
    cover: "https://ziyaratguide.com/wp-content/uploads/2025/09/ImamHussain2.avif",
    tags: ["Ziyarat", "Karbala", "Travel Guide"],
    seoTitle:
      "Ziyarat of Imam Hussain (AS) in Karbala | Complete Guide & Travel Tips",
    seoDescription:
      "Plan your ziyarat to Imam Hussain’s shrine in Karbala. History, shrine details, dua, etiquette, safety tips, and travel guide for pilgrims visiting Iraq.",
    body: `
<h1>A Complete Guide to Ziyarat of Imam Hussain (AS) in Karbala</h1>

<p>Visiting the blessed shrine of Imam Hussain (عليه السلام) in Karbala is not just a journey — it is a calling, a spiritual experience that transforms the heart.</p>

<p>Millions travel every year to stand beside his holy roza and say:</p>
<blockquote>السلام عليك يا أبا عبد الله الحسين</blockquote>

<p>Imam Hussain (AS) is the symbol of courage, justice, and dignity. His stand in Karbala inspires humanity to this day.</p>

<h2>Who is Imam Hussain (AS)?</h2>
<p>Imam Hussain ibn Ali (AS) is the grandson of Prophet Muhammad ﷺ, the son of Imam Ali (AS) and Lady Fatimah (AS), and the brother of Imam Hasan (AS). He rose against injustice and was martyred in Karbala, Iraq, in 680 AD (61 AH).</p>

<h2>About the Shrine (Roza Imam Hussain)</h2>
<p>The Roza of Imam Hussain (AS) in Karbala is one of the holiest sites in the world.</p>

<ul>
  <li>Zari / Dha'rat Al-Imam (Zarih - enclosure of the grave)</li>
  <li>Sahn Al-Abbas (courtyard)</li>
  <li>Musalla (prayer halls)</li>
  <li>Museum and library</li>
  <li>Turbah al-Hussain (sacred soil) area</li>
</ul>

<h2>Spiritual Etiquettes (Adab)</h2>
<p>Before entering the shrine, say: <strong>Bismillah wa billah, wa 'ala millati Rasoolillah ﷺ.</strong></p>
<p>Inside, follow these etiquettes:</p>
<ul>
  <li>Dress modestly</li>
  <li>Move quietly</li>
  <li>Respect others praying</li>
  <li>Avoid loud photography</li>
  <li>Recite salawat peacefully</li>
</ul>

<h2>Must-Read Ziyarat Dua</h2>
<blockquote>
السلام عليك يا أبا عبد الله، وعلى الأرواح التي حلت بفنائك  
<br/>Peace be upon you, O Aba Abdillah, and upon the souls who camped in your courtyard.
</blockquote>

<ul>
  <li>Surah Al-Fatiha</li>
  <li>Surah Ya-Sin</li>
  <li>Salawat (اللهم صل على محمد وآل محمد)</li>
</ul>

<h2>Best Time to Visit Karbala</h2>
<p>You may visit any time, but the most blessed periods include:</p>

<table>
  <thead>
    <tr><th>Season</th><th>Significance</th></tr>
  </thead>
  <tbody>
    <tr><td>Muharram & Ashura</td><td>Marking the tragedy of Karbala</td></tr>
    <tr><td>Arbaeen</td><td>Largest peaceful pilgrimage on earth</td></tr>
    <tr><td>Ramadan</td><td>Month of spiritual elevation</td></tr>
    <tr><td>Sha’ban</td><td>Birth anniversaries of Ahlulbayt (AS)</td></tr>
  </tbody>
</table>

<h2>Travel Guide: How to Reach Karbala</h2>
<ul>
  <li>Najaf International Airport (1 hr drive)</li>
  <li>Baghdad Airport (1 hr 45 min drive)</li>
</ul>
<p>Choose hotels near the shrine for easier access.</p>

<h2>Where to Stay</h2>
<ul>
  <li>Babu Baghdad Street</li>
  <li>Al-Abbas Street</li>
  <li>Near Sahn Imam Abbas (AS)</li>
</ul>

<h2>Dress Code & Essentials</h2>
<table>
  <thead>
    <tr><th>Item</th><th>Notes</th></tr>
  </thead>
  <tbody>
    <tr><td>Modest clothing</td><td>Abaya recommended for ladies</td></tr>
    <tr><td>Comfortable footwear</td><td>Long walking distances</td></tr>
    <tr><td>Water bottle</td><td>Stay hydrated</td></tr>
    <tr><td>Tasbih / Prayer mat</td><td>Useful during busy times</td></tr>
    <tr><td>Portable phone battery</td><td>Long ziyarat hours</td></tr>
  </tbody>
</table>

<h2>Safety and General Tips</h2>
<ul>
  <li>Stay in pilgrimage zones</li>
  <li>Respect local customs</li>
  <li>Avoid late-night unnecessary travel</li>
</ul>

<h2>Final Reflection</h2>
<p>Ziyarat of Imam Hussain (AS) brings peace, humility, and strength. You return not the same — you return with a heart enlightened by sacrifice and devotion.</p>

<blockquote>كل أرض كربلاء، وكل يوم عاشوراء<br/>Every land can be Karbala, and every day Ashura — a reminder to stand for justice always.</blockquote>
    `,
  },

  {
    id: 2,
    slug: "imam-hussain-family-tree-quotes-lessons",
    title: "Imam Hussain (AS) - Quotes, Family Tree & Timeless Lessons",
    excerpt:
      "Explore the wisdom of Imam Hussain, his noble family tree, powerful quotes, and the universal lessons from Karbala for justice, dignity, and humanity.",
    cover: "https://ziyaratguide.com/wp-content/uploads/2025/09/ImamHussain3.avif",
    tags: ["Imam Hussain", "Ahlulbayt", "Quotes"],
    seoTitle: "Imam Hussain (AS) Quotes, Family Tree & Lessons from Karbala",
    seoDescription:
      "Explore the wisdom of Imam Hussain, his noble family tree, powerful quotes, and the universal lessons from Karbala for justice, dignity, and humanity.",
    body: `
<h1>Imam Hussain (AS) - Quotes, Family Tree & Timeless Lessons</h1>

<p>Imam Hussain (AS) is not only a historical figure — he is a living lesson for humanity. His message stands across centuries:</p>
<blockquote>Stand with truth. Never bow to oppression.</blockquote>

<h2>The Noble Lineage</h2>

<table>
  <thead>
    <tr><th>Category</th><th>Names</th></tr>
  </thead>
  <tbody>
    <tr><td>Grandfather</td><td>Prophet Muhammad PBUH</td></tr>
    <tr><td>Grandmother</td><td>Lady Khadijah (AS)</td></tr>
    <tr><td>Parents</td><td>Imam Ali (AS) & Lady Fatimah (AS)</td></tr>
    <tr><td>Siblings</td><td>Imam Hasan (AS), Lady Zainab (AS), Lady Umm Kulthum (AS)</td></tr>
    <tr><td>Wives</td><td>Lady Shahrbanu (AS), Lady Rabab (AS)</td></tr>
    <tr><td>Children</td><td>Ali Zain-ul-Abidin (AS), Ali Akbar (AS), Abdullah (AS), Ruqayya (AS), Sakina (AS)</td></tr>
  </tbody>
</table>

<h2>Inspiring Quotes of Imam Hussain (AS)</h2>

<blockquote>“I did not rise for fame or oppression, but to reform the nation of my grandfather.”</blockquote>
<blockquote>“Death with dignity is better than life with humiliation.”</blockquote>
<blockquote>“The most generous person is the one who gives help to those who do not expect it.”</blockquote>
<blockquote>“If you have no religion, at least be free men in this world.”</blockquote>

<h2>Lessons from Karbala</h2>

<table>
  <thead>
    <tr><th>Lesson</th><th>Meaning</th></tr>
  </thead>
  <tbody>
    <tr><td>Stand for justice</td><td>Even when alone</td></tr>
    <tr><td>Value truth</td><td>Truth prevails overpower</td></tr>
    <tr><td>Respect women</td><td>The role of Lady Zainab (AS)</td></tr>
    <tr><td>Patience</td><td>Sabr in the face of pain</td></tr>
    <tr><td>Unity</td><td>Hussain’s message belongs to all humanity</td></tr>
  </tbody>
</table>

<p>Karbala is not about war. It is about values, humanity, and honour.</p>

<h2>Why Millions Still Visit</h2>
<p>Every year, millions walk to Karbala for Arbaeen. It is the largest peaceful gathering in the world. People host strangers, give free food, medical care, and shelter. This is love. This is Hussain.</p>

<blockquote>يا ليتنا كنا معكم فنفوز فوزًا عظيما<br/>How we wish we were there with you, to achieve that great triumph.</blockquote>
    `,
  },
];
